import React, {
  ReactNode,
  createContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Branding, Children, Logos, BrandColors } from 'types';
import { Color, Components, Theme, Shadows, CssBaseline } from '@mui/material';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router';
import { LinkProps } from '@mui/material/Link';
import { PaletteOptions } from '@mui/material/styles';
import { deleteCookie, getCookieValue, logError, setCookieValue } from 'utils';
import { getBranding } from 'api/branding';
import { TypographyVariantsOptions } from '@mui/material/styles';

export interface ThemeContext {
  logos: Logos;
  setLogo: (params: SetLogoParams) => void;
  setBannerLogo: (params: SetLogoParams) => void;
  setTenantBranding: (params: SetTenantBrandingParams) => void;
  updatePalette: (params: UpdatePaletteParams) => void;
}

export interface UpdatePaletteParams {
  primary: string | undefined;
  secondary: string | undefined;
}
export interface SetLogoParams {
  url: string | undefined;
  padding: number | undefined;
}
export interface SetTenantBrandingParams {
  branding: Branding | undefined;
  companyPath: string | undefined;
}

export const LinkBehavior = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }
>((props, ref) => {
  const { href, ...other } = props;
  return <RouterLink ref={ref} to={href} {...other} />;
});
LinkBehavior.displayName = 'LinkBehavior';

// Brand Colors
export const AUTURA_AZURE = '#192EF9'; // secondary (headers)
export const AUTURA_CONCRETE = '#EEEDE8'; // paper
export const AUTURA_AZURE_BACKGROUND = 'rgba(25, 46, 249, 0.06)';

export const AUTURA_SUCCESS = '#17B26A';
export const AUTURA_WARNING = '#F79009';
export const AUTURA_ERROR = '#F04438';

export const AUTURA_ASPHALT_LIGHTEST = '#F4F4F4';
export const AUTURA_ASPHALT_LIGHTER = '#E9E9E9';
export const AUTURA_ASPHALT_LIGHT = '#CACACA';
export const AUTURA_ASPHALT = '#232323';
export const AUTURA_ASPHALT_DARK = '#727272';
export const AUTURA_ASPHALT_DARKER = '#3D3D3D';
export const AUTURA_ASPHALT_DARKEST = '#000000';

export const TRAXERO_ORANGE = '#FF8200';
export const TRAXERO_NAVY = '#1F2534';

export const AUTURA_GRAYSCALE: Partial<Color> = {
  50: '#F9FAFB',
  100: '#F2F4F7',
  200: '#EAECF0',
  300: '#D0D5DD',
  400: '#98A2B3',
  500: '#667085',
  600: '#475467',
  700: '#344054',
  800: '#182230',
  900: '#101828',
  A100: '#F2F4F7',
  A200: '#EAECF0',
  A400: '#98A2B3',
  A700: '#344054',
};

export const AUTURA_PALETTE: PaletteOptions = {
  mode: 'light',
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  background: {
    default: AUTURA_AZURE_BACKGROUND,
    paper: AUTURA_GRAYSCALE[100],
  },
  grey: AUTURA_GRAYSCALE,
  primary: {
    main: AUTURA_AZURE,
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: AUTURA_ASPHALT,
    contrastText: '#FFFFFF',
  },
  info: {
    main: AUTURA_AZURE,
    contrastText: '#FFFFFF',
  },
  success: {
    main: AUTURA_SUCCESS,
    contrastText: '#FFFFFF',
  },
  warning: {
    main: AUTURA_WARNING,
    contrastText: '#FFFFFF',
  },
  error: {
    main: AUTURA_ERROR,
    contrastText: '#FFFFFF',
  },
  divider: AUTURA_GRAYSCALE[700],
};
export const AUTURA_SHADOWS: Shadows = [
  'none',
  '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
  '0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)',
  '0px 4px 8px -2px rgba(16, 24, 40, 0.10), 0px 2px 4px -2px rgba(16, 24, 40, 0.06)',
  '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
  '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
  '0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
  '0px 32px 64px -12px rgba(16, 24, 40, 0.14)',
  '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
  '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
  '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
  '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
  '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
  '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
  '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
  '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
  '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
  '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
  '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
  '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
  '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
  '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
  '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
  '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
  '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
];

// Brand Typography
const FONT_WEIGHT_LIGHT = 300;
const FONT_WEIGHT_REGULAR = 400;
const FONT_WEIGHT_MEDIUM = 500;
const FONT_WEIGHT_BOLD = 700;
const FONT_FAMILY = '"Roboto", sans-serif';
export const TEXT_PRIMARY = '#101828';

export const AUTURA_TYPOGRAPHY: TypographyVariantsOptions = {
  htmlFontSize: 16,
  fontFamily: FONT_FAMILY,
  fontSize: 16,
  fontWeightLight: FONT_WEIGHT_LIGHT,
  fontWeightRegular: FONT_WEIGHT_REGULAR,
  fontWeightMedium: FONT_WEIGHT_MEDIUM,
  fontWeightBold: FONT_WEIGHT_BOLD,
  h1: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_LIGHT,
    fontSize: '2.25rem',
    lineHeight: '2.75rem',
    letterSpacing: '-0.02em',
    color: TEXT_PRIMARY,
  },
  h2: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: '1.875rem',
    lineHeight: '2.375rem',
    letterSpacing: '0.0075em',
    color: TEXT_PRIMARY,
  },
  h3: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: '1.5rem',
    lineHeight: '2rem',
    letterSpacing: '0.0075em',
    color: TEXT_PRIMARY,
  },
  h4: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: '1.25rem',
    lineHeight: '1.875rem',
    letterSpacing: '0.00938em',
    color: TEXT_PRIMARY,
  },
  h5: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_REGULAR,
    fontSize: '1.125rem',
    lineHeight: '1.75rem',
    letterSpacing: '0.00714em',
    color: TEXT_PRIMARY,
  },
  h6: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: '1rem',
    lineHeight: '1.75rem',
    letterSpacing: '0.00938em',
    color: TEXT_PRIMARY,
  },
  body1: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_REGULAR,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: '0.00938em',
    color: TEXT_PRIMARY,
  },
  body2: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_REGULAR,
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    letterSpacing: '0.01071em',
    color: TEXT_PRIMARY,
  },
  subtitle1: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_REGULAR,
    fontSize: '1rem',
    lineHeight: '1.25rem',
    letterSpacing: '0.01071em',
    color: TEXT_PRIMARY,
  },
  subtitle2: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_REGULAR,
    fontSize: '0.875rem',
    lineHeight: '1rem',
    letterSpacing: '0.00938em',
    color: TEXT_PRIMARY,
  },
  button: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: '0.875rem',
    lineHeight: '1.75rem',
    letterSpacing: '0.02857em',
    textTransform: 'capitalize',
    color: TEXT_PRIMARY,
  },
  caption: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_REGULAR,
    fontSize: '0.75rem',
    lineHeight: '1rem',
    letterSpacing: '0.03333em',
    opacity: 0.8,
    color: TEXT_PRIMARY,
  },
  overline: {
    fontFamily: FONT_FAMILY,
    fontWeight: FONT_WEIGHT_REGULAR,
    fontSize: '0.75rem',
    lineHeight: '2.66rem',
    letterSpacing: '0.08333em',
    textTransform: 'uppercase',
    color: TEXT_PRIMARY,
  },
};
export const COMPONENT_OVERRIDES: Components = {
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        height: '100%',
        width: '100%',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        position: 'fixed',
        overscrollBehavior: 'none',
        minHeight: 0,
        webkitOverflowScrolling: 'touch', // For iOS momentum scrolling
      },
      body: {
        height: '100%',
        width: '100%',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        position: 'fixed',
        overscrollBehavior: 'none',
        webkitOverflowScrolling: 'touch',
        minHeight: 0,
        // Prevent text selection during touch/scroll operations
        webkitUserSelect: 'none',
        userSelect: 'none',
        // Prevent touch callouts on long press (iOS)
        webkitTouchCallout: 'none',
      },
      '#root': {
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'fixed',
        minHeight: 0,
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        paddingLeft: 0,
        paddingRight: 0,
        overflowX: 'hidden',
      },
    },
  },
  MuiTableSortLabel: {
    styleOverrides: {
      root: {
        color: 'white !important',
        '& .MuiTableSortLabel-icon': {
          color: 'white !important',
        },
        '& .MuiTableSortLabel-iconDirectionAsc': {
          color: 'white',
        },
      },
      icon: {
        color: 'white',
      },
    },
  },
  MuiLink: {
    defaultProps: {
      color: 'info.main',
      textTransform: 'uppercase',
      component: LinkBehavior,
    } as LinkProps,
  },
  MuiButtonBase: {
    defaultProps: {
      LinkComponent: LinkBehavior,
      disableRipple: true,
    },
  },
  MuiTableFooter: {
    defaultProps: {
      sx: {
        '& .MuiTableCell-root': {
          borderTop: '2px solid',
          borderTopColor: 'secondary.main',
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        color: 'rgba(0, 0, 0, 0.8)',
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        maxWidth: 'calc(100% - 24px)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  },
  MuiUseMediaQuery: {
    defaultProps: {
      noSsr: true,
    },
  },
};

export const DEFAULT_BRANDING: Branding = {
  logoUrl: '/traxero_ppi_dark.png',
  logoPadding: 16,
  bannerLogoUrl: '/traxero_ppi.png',
  bannerPadding: 16,
  primary: AUTURA_AZURE,
  secondary: AUTURA_ASPHALT,
};
export const DEFAULT_THEME = createTheme({
  palette: AUTURA_PALETTE,
  typography: AUTURA_TYPOGRAPHY,
  shadows: AUTURA_SHADOWS,
  components: COMPONENT_OVERRIDES,
});
export const BASELINE_PROPERTY_BRANDING: Branding = {
  primary: AUTURA_ASPHALT,
  secondary: AUTURA_AZURE,
  logoUrl: '',
  logoPadding: 0,
  bannerLogoUrl: '',
  bannerPadding: 0,
};

export const getBackgroundForColor = (hex: string): string => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Convert hex to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, 0.05)`;
};
export const getContrastColor = (color: string): '#000000' | '#FFFFFF' => {
  if (!color) {
    return '#000000';
  }

  if (color === TRAXERO_ORANGE || color === TRAXERO_NAVY) {
    return '#FFFFFF';
  }

  // Convert hex color to RGB
  if (color.startsWith('#')) {
    color = color.slice(1);
    const r = parseInt(color.slice(0, 2), 16);
    const g = parseInt(color.slice(2, 4), 16);
    const b = parseInt(color.slice(4, 6), 16);
    color = `rgb(${r}, ${g}, ${b})`;
  }

  // Extract the RGB values
  const rgb = color.match(/(\d+),\s*(\d+),\s*(\d+)/);
  if (!rgb) {
    logError(
      new Error(
        'invalid color input, please provide a valid hex or rgb color code',
      ),
    );
    return '#000000';
  }
  const [r, g, b] = [parseInt(rgb[1]), parseInt(rgb[2]), parseInt(rgb[3])];

  // Calculate the luminance of the color
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return 'black' for light colors and 'white' for dark colors
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

export const getHighlightColorFromHex = (color: string): string => {
  // Convert hex color to RGB
  if (color.startsWith('#')) {
    color = color.slice(1);
    const r = parseInt(color.slice(0, 2), 16);
    const g = parseInt(color.slice(2, 4), 16);
    const b = parseInt(color.slice(4, 6), 16);
    color = `rgba(${r}, ${g}, ${b}, 0.1)`; // Add 0.1 alpha value
    return color;
  } else {
    // fallback to whatever they sent
    return color;
  }
};

export const getBrandedTheme = (
  brandColors: BrandColors | undefined,
): Theme => {
  return createTheme({
    ...DEFAULT_THEME,
    palette: {
      ...DEFAULT_THEME.palette,
      background: {
        default: getBackgroundForColor(brandColors?.primary || AUTURA_AZURE),
        paper: AUTURA_GRAYSCALE[100],
      },
      primary: {
        main: brandColors?.primary || AUTURA_AZURE,
        contrastText: getContrastColor(brandColors?.primary || AUTURA_AZURE),
      },
      secondary: {
        main: brandColors?.secondary || AUTURA_ASPHALT,
        contrastText: getContrastColor(
          brandColors?.secondary || AUTURA_ASPHALT,
        ),
      },
    },
  });
};

// Create a context for the theme
export const ThemeContext = createContext({} as ThemeContext);

export const getInitialTheme = (): Theme => {
  const primary = getCookieValue('primary') || DEFAULT_BRANDING.primary;
  const secondary = getCookieValue('secondary') || DEFAULT_BRANDING.secondary;

  return getBrandedTheme({
    primary,
    secondary,
  });
};

export const getInitialLogos = (): Logos => {
  const logoUrl = getCookieValue('logoUrl') || DEFAULT_BRANDING.logoUrl;
  const logoPadding =
    parseInt(getCookieValue('logoPadding')) || DEFAULT_BRANDING.logoPadding;
  const bannerLogoUrl =
    getCookieValue('bannerLogoUrl') || DEFAULT_BRANDING.bannerLogoUrl;
  const bannerPadding =
    parseInt(getCookieValue('bannerPadding')) || DEFAULT_BRANDING.bannerPadding;

  return {
    logoUrl,
    logoPadding,
    bannerLogoUrl,
    bannerPadding,
  };
};

// Mobile Layout Variables
export const MOBILE_LAYOUT = {
  navbarHeight: '100px',
  footerHeight: '50px',
  contentPadding: '16px',
} as const;

// Create a provider component
const ThemeContextProvider = ({ children }: Children): ReactNode => {
  const [theme, setTheme] = useState<Theme>(getInitialTheme());
  const [logos, setLogos] = useState<Logos>(getInitialLogos());

  useEffect(() => {
    const fetchBranding = async (companyPath: string) => {
      if (companyPath) {
        const branding = await getBranding(companyPath);
        setTenantBranding({ branding, companyPath });
      }
    };
    fetchBranding(getCookieValue('companyPath'));
  }, []);

  // Function to update the theme
  const updatePalette = useCallback(
    ({ primary, secondary }: UpdatePaletteParams): void => {
      const brandColors = {
        primary:
          primary || getCookieValue('primary') || DEFAULT_BRANDING.primary,
        secondary:
          secondary ||
          getCookieValue('secondary') ||
          DEFAULT_BRANDING.secondary,
      };
      setTheme(getBrandedTheme(brandColors));
    },
    [],
  );

  const setBannerLogo = useCallback(
    ({ url, padding }: SetLogoParams) => {
      const _url =
        url ||
        getCookieValue('bannerLogoUrl') ||
        DEFAULT_BRANDING.bannerLogoUrl;
      let _padding =
        padding ||
        parseInt(getCookieValue('bannerPadding')) ||
        DEFAULT_BRANDING.bannerPadding;

      // If padding is 0, set it to 0 instead of default
      if (padding === 0) {
        _padding = 0;
      }

      setLogos({
        ...logos,
        bannerLogoUrl: _url,
        bannerPadding: _padding,
      });
    },
    [logos.bannerLogoUrl, logos.bannerPadding],
  );

  const setLogo = useCallback(
    ({ url, padding }: SetLogoParams) => {
      const _url = url || getCookieValue('logoUrl') || DEFAULT_BRANDING.logoUrl;
      let _padding =
        padding ||
        parseInt(getCookieValue('logoPadding')) ||
        DEFAULT_BRANDING.logoPadding;

      // If padding is 0, set it to 0 instead of default
      if (padding === 0) {
        _padding = 0;
      }

      setLogos({
        ...logos,
        logoUrl: _url,
        logoPadding: _padding,
      });
    },
    [logos.logoUrl, logos.logoPadding],
  );

  const setTenantBranding = useCallback(
    ({ branding, companyPath }: SetTenantBrandingParams) => {
      setTheme(getBrandedTheme(branding));
      setLogos({
        bannerLogoUrl:
          branding?.bannerLogoUrl || DEFAULT_BRANDING.bannerLogoUrl,
        bannerPadding:
          branding?.bannerPadding === undefined
            ? DEFAULT_BRANDING.bannerPadding
            : branding?.bannerPadding,
        logoUrl: branding?.logoUrl || DEFAULT_BRANDING.logoUrl,
        logoPadding:
          branding?.logoPadding === undefined
            ? DEFAULT_BRANDING.logoPadding
            : branding?.logoPadding,
      });

      setCookieValue('primary', branding?.primary || DEFAULT_BRANDING.primary);
      setCookieValue(
        'secondary',
        branding?.secondary || DEFAULT_BRANDING.secondary,
      );
      setCookieValue('logoUrl', branding?.logoUrl || DEFAULT_BRANDING.logoUrl);
      setCookieValue(
        'logoPadding',
        branding?.logoPadding?.toString() ||
          DEFAULT_BRANDING.logoPadding.toString(),
      );
      setCookieValue(
        'bannerLogoUrl',
        `${branding?.bannerLogoUrl || DEFAULT_BRANDING.bannerLogoUrl}`,
      );
      setCookieValue(
        'bannerPadding',
        `${branding?.bannerPadding || DEFAULT_BRANDING.bannerPadding}`,
      );

      if (companyPath) {
        setCookieValue('companyPath', companyPath);
      } else {
        deleteCookie('companyPath');
      }
    },
    [],
  );

  return (
    <ThemeContext.Provider
      value={{
        updatePalette,
        setBannerLogo,
        setLogo,
        setTenantBranding,
        logos,
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContextProvider;
